import React, { useState, useContext, memo, RefObject } from 'react';
import styled from '@emotion/styled';
import Form from '@rjsf/core';
import { selectValidation } from '@/redux/selectors/orderPlacementSelectors';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, Fade, FormHelperText, Grid, Typography, darken, lighten } from '@mui/material';
import { ConfigContext } from '@/contextAPI/configContext';
import TitleVersionChip from '@/components/_common/TitleVersionChip';
import { getLineageMetaTitle as getLineageMetaTitleDefault } from '@/_utils/utils';
import DateTimeDisplay from '@/components/_common/DateTimeDisplay';
import DeleteRowButton from '@/components/_common/DeleteRowButton';
import PolarisIcon from '@/components/_common/PolarisIcon';
import type { JobDraft, TitleEntity } from '@/redux/slices/slice_types';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { useAppSelector } from '@/redux/hooks';
import OPJobProfileDetails from './OPJobProfileDetails';
import EditableDate from '@/components/_common/EditableDate';
import { selectCanWriteJobs } from '@/redux/selectors/authSelectors';
import { selectTitleMetadataById } from '@/redux/selectors/titleV2Selectors';
import { OrderPlacementConfig } from '@/__configs/config_types';
import { useTranslation } from 'react-i18next';

interface Props {
  isSelected?: boolean;
  hideSelectAndDelete?: boolean;
  job: JobDraft;
  onSelectedChange: (isSelected: boolean) => void;
  onUpdateJob: (job: JobDraft) => void;
  onDeleteJob: (job: JobDraft) => void;
  setHaveJobsBeenEdited: (value: boolean) => void;
  schemaRef: RefObject<Form>;
}

// type guard function, needed just to make sure that we pass the argument of the correct type to the `getLineageMetaTitleDefault` function
const isVersion = (version: TitleEntity | object): version is TitleEntity => {
  return typeof (version as TitleEntity).id !== 'undefined';
};

// STEP 3
const OPJobAccordion = ({
  isSelected = false,
  hideSelectAndDelete = false,
  job = {} as JobDraft,
  onSelectedChange,
  onUpdateJob,
  onDeleteJob,
  setHaveJobsBeenEdited,
  schemaRef
}: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const validation = useAppSelector(selectValidation);
  const hasError = validation[job.id] && Object.keys(validation[job.id])?.length > 0;
  const { profile = {}, version = {}, due_date, embargo_date } = job as JobDraft;
  const type = (profile as JobDraft['profile'])?.data?.workflow_type || 'version_based';
  const getLineageMetaTitle = useContext(ConfigContext)?.orderPlacementConfig?.getLineageMetaTitle;
  const titleName = getLineageMetaTitle ? getLineageMetaTitle(version) : getLineageMetaTitleDefault(isVersion(version) ? version : undefined);
  const disableDueDate = useConfigValue(['orderPlacementConfig', 'disableDueDate']);
  const disableEmbargoDate = useConfigValue(['orderPlacementConfig', 'disableEmbargoDate']);
  const renderMetadata: OrderPlacementConfig['renderMetadata'] = useConfigValue(['orderPlacementConfig', 'renderMetadata']);
  const disableBulkEdit = useConfigValue(['orderPlacementConfig', 'disableBulkEdit']);
  const metadata = useAppSelector((state) => selectTitleMetadataById(state, job?.version?.parent));

  const canWriteJobs = useAppSelector(selectCanWriteJobs);

  const toggleAccordion = () => {
    setExpanded(!expanded);
  };

  const deleteJob = () => {
    onDeleteJob(job);
    setHaveJobsBeenEdited(true);
  };

  const handleIsSelectedChange = () => onSelectedChange(!isSelected);

  // This event handler is required to prevent Checkboxes parent Accordion component from expanding/collapsing when checkbox is clicked.
  const handleCheckboxClick = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <Fade appear in>
      <JobAccordion error={hasError} disableGutters expanded={expanded}>
        <StyledAccordionSummary aria-controls="profile-detail" onClick={toggleAccordion} expandIcon={<ExpandIcon width={25} icon="right" />}>
          <ScrollableDiv>
            {!hideSelectAndDelete && !disableBulkEdit && (
              <CellWrapper>
                <Checkbox checked={isSelected} onClick={handleCheckboxClick} onChange={handleIsSelectedChange} />
              </CellWrapper>
            )}
            <CellWrapper style={{ minWidth: 280 }}>
              <Grid container item flexWrap="nowrap">
                <PolarisIconBase width={20} type={type} icon={type} style={{ marginTop: 3, marginRight: 5 }} />
                <ProfileName type={type} variant="h6" noWrap>
                  {(profile as JobDraft['profile'])?.name}
                </ProfileName>
              </Grid>
              <Typography variant="body2">
                Customer: <strong>{job?.customer}</strong>
              </Typography>
            </CellWrapper>
            <CellWrapper style={{ minWidth: 300, flexGrow: 1, paddingRight: 20 }}>
              <StyledTitleVersionChip version={version as TitleEntity} clickable={false} size="small" withTooltip />
              <Typography variant="h6" sx={{ ml: 1 }}>
                {titleName}
              </Typography>
            </CellWrapper>
            {metadata && <CellWrapper style={{ minWidth: 280 }}>{renderMetadata?.(metadata)}</CellWrapper>}
            {disableDueDate ? (
              <FormHelperText sx={{ lineHeight: 1 }}>Externally managed</FormHelperText>
            ) : (
              <CellWrapper>
                <StyledEditableDate
                  skipConfirmation
                  label={
                    <LabelDiv>
                      <DueDatePolarisIcon width={19} icon="calendar" style={{ marginTop: 2 }} />
                      <span>Due Date</span>
                    </LabelDiv>
                  }
                  key="due_date"
                  color="primary"
                  onEdit={(key: string, value: JobDraft[keyof JobDraft]) => {
                    onUpdateJob({ ...job, [key]: value });
                  }}
                  onEditFormatter={(date: Date) => date.toISOString()}
                  disabled={!canWriteJobs}
                  onEditKey="due_date"
                  value={due_date ?? undefined}
                  onClick={(e) => e.stopPropagation()}
                  portalId="datepicker-popper-id"
                >
                  <StyledDateTimeDisplay singleLine missingText="-">
                    {due_date}
                  </StyledDateTimeDisplay>
                </StyledEditableDate>
              </CellWrapper>
            )}
            {!disableEmbargoDate && (
              <CellWrapper>
                <StyledEditableDate
                  skipConfirmation
                  label={
                    <LabelDiv alternate>
                      <DueDatePolarisIcon width={21} icon="calendar_clock" style={{ marginTop: 2 }} />
                      <span>{t('Embargo Date')}</span>
                    </LabelDiv>
                  }
                  key="embargo_date"
                  color="primary"
                  onEdit={(key: any, value: any) => {
                    onUpdateJob({ ...job, [key]: value });
                  }}
                  onEditFormatter={(date: Date) => date.toISOString()}
                  disabled={!canWriteJobs}
                  onEditKey="embargo_date"
                  value={due_date ?? undefined}
                  onClick={(e) => e.stopPropagation()}
                  portalId="datepicker-popper-id"
                >
                  <StyledDateTimeDisplay singleLine missingText="-">
                    {embargo_date}
                  </StyledDateTimeDisplay>
                </StyledEditableDate>
              </CellWrapper>
            )}
            {!hideSelectAndDelete && (
              <DeleteWrapper>
                <DeleteRowButton onClick={deleteJob} confirmation />
              </DeleteWrapper>
            )}
          </ScrollableDiv>
        </StyledAccordionSummary>
        <JobAccordionDetails>
          <OPJobProfileDetails job={job} onUpdateJob={onUpdateJob} setHaveJobsBeenEdited={setHaveJobsBeenEdited} schemaRef={schemaRef} />
        </JobAccordionDetails>
      </JobAccordion>
    </Fade>
  );
};

export default memo(OPJobAccordion);

const JobAccordionDetails = styled(AccordionDetails)`
  height: 100%;
  border-bottom-left-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border-bottom-right-radius: ${({ theme }) => theme.shape.borderRadius}px;

  &.MuiAccordionDetails-root {
    border-top: ${({ theme }) => (theme.palette.mode === 'light' ? `1px solid ${theme.palette.divider}` : 0)};
    padding: 0;
  }
`;

const JobAccordion = styled(Accordion)<{
  error?: boolean;
}>`
  background: ${({ theme }) => (theme.palette.mode === 'light' ? theme.palette.background.paper : lighten(theme.palette.background.paper, 0.03))};
  & .MuiTypography-h6 {
    font-size: 1.1rem;
  }

  ${({ theme, error }) =>
    error
      ? `
    & .MuiAccordionSummary-root:before {
      content: "";
      position: absolute;
      left: 0;
      width: 3px;
      height: 100%;
      background-color: ${theme.palette.error.main};
      border-top-left-radius: ${theme.shape.borderRadius}px;
      border-bottom-left-radius: ${theme.shape.borderRadius}px;
    }
  `
      : null};
`;

const ExpandIcon = styled(PolarisIcon)`
  cursor: pointer;
  margin-left: 15px;
`;

const PolarisIconBase = styled(PolarisIcon)<{
  type: 'component_based' | 'version_based' | 'bulk' | 'package' | 'version_based_bundle' | 'component_based_bundle';
}>`
  ${({ theme, type }) =>
    type === 'component_based'
      ? `
    color: ${theme.palette.lavender.main};
  `
      : `
    color: ${theme.palette.teal.main};
  `}
`;

const ProfileName = styled(Typography)<{
  type: 'component_based' | 'version_based' | 'bulk' | 'package' | 'version_based_bundle' | 'component_based_bundle';
}>`
  ${({ theme, type }) =>
    type === 'component_based' || type === 'component_based_bundle'
      ? `
    color: ${theme.palette.lavender.main};
  `
      : `
    color: ${theme.palette.teal.main};
  `}
`;

const DueDatePolarisIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

const LabelDiv = styled.div<{ alternate?: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};
  flex-wrap: nowrap;
  font-size: 1.1rem;
  height: 20px;

  ${({ theme, alternate }) =>
    alternate &&
    `
  color: ${theme.palette.default.main};
  svg {
    color: ${theme.palette.default.main};
  }
  `}
`;

const StyledEditableDate = styled(EditableDate)`
  padding: 0;
  width: 100%;
  min-width: 200px;
  margin-right: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  height: 100%;

  & > div:nth-of-type(2) {
    padding: 0;
    min-width: 200px;
  }
`;

const StyledDateTimeDisplay = styled(DateTimeDisplay)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const StyledTitleVersionChip = styled(TitleVersionChip)`
  min-width: 50px;
  text-overflow: ellipsis;
  margin-left: 0;
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  background: ${({ theme }) =>
    theme.palette.mode === 'light' ? darken(theme.palette.background.default, 0.005) : lighten(theme.palette.background.paper, 0.08)};
  background: ${({ theme }) => theme.customOverrides?.AddedJobAccordion?.backgroundColor};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;

  &.Mui-expanded {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    &:before {
      border-bottom-left-radius: 0;
    }
  }

  .MuiAccordionSummary-root {
    padding: ${({ theme }) => theme.spacing(1, 2)};
  }

  .MuiAccordionSummary-content {
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const ScrollableDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  column-gap: ${({ theme }) => theme.spacing(2)};
`;

const DeleteWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(0, 2)};
`;

const CellWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(1.5, 0)};
`;
